import React, { useEffect, useState } from 'react';

import { Badge } from 'antd';

import styled from 'styled-components';

import { showDateNoTime } from '@libs/NwMoment';

import { useDeleteContactConnection } from '@hooks/API/Contact/UseContactConnections';

import AddressForm from '@components/Connection/AddressForm';
import BaseItem from '@components/Connection/BaseItem';
import ConnectionDetailForm from '@components/Connection/ConnectionDetailForm';
import EmailForm from '@components/Connection/EmailForm';
import PhoneForm from '@components/Connection/PhoneForm';
import FlexContainer from '@components/Gui/FlexContainer';
import { NwCard } from '@components/Gui/NwCard/NwCard';
import NwDivider from '@components/Gui/NwDivider';
import NwDrawer from '@components/Gui/NwDrawer';
import NwDropDownMenu from '@components/Gui/NwDropDownMenu';
import NwIcon from '@components/Gui/NwIcon';
import StyleVariables from '@components/Gui/StyleVariables';

import { byPrefixAndName } from '@awesome.me/kit-d6dae216cd/icons';

const BaseItemWrapper = styled.div`
    padding-left: 24px;
`;

const ConnectionCard = ({ onSelect, item, readOnly, contactID, onAddToMailingList }) => {
    const [mode, setMode] = useState('SHOW');
    const [addingNewItem, setAddingNewItem] = useState(null); // ADDRESS - PHONE - EMAIL - CONNECTION
    const [touchForm, setTouchForm] = useState(false);
    const { mutate: deleteConnection } = useDeleteContactConnection();

    useEffect(() => {
        if (item && onSelect) {
            onSelect(item.ID);
        }
    }, [item]);

    const ConnectionCardTitleSecondLine = () => {
        let line = item.SubTypeName || '';
        if (item.Role) {
            if (line.length) {
                line += ' - ';
            }
            line += item.Role;
        }
        return line;
    };

    const ConnectionCardTitle = () => (
        <div className="card-header">
            <div className="title">
                {item.Name} {item.Surname || ''}
            </div>
            {item.TypeName !== 'Cc' && <div className="label">{ConnectionCardTitleSecondLine()}</div>}
        </div>
    );

    const renderTypeIcon = () => {
        switch (item.TypeName) {
            case 'Personal':
                return byPrefixAndName.fasl['home-heart'];
            case 'Place':
                return byPrefixAndName.fasl['home-alt'];
            case 'People':
                return byPrefixAndName.fasl['user'];
            case 'Cc':
                return byPrefixAndName.fasl['user-plus'];
            case 'Room':
                return byPrefixAndName.fasl['bed'];
            default:
                return '';
        }
    };

    const deleteElement = () => {
        deleteConnection({ connectionID: item.ID, contactID: contactID });
    };

    const renderMenuSubItem = () =>
        !readOnly ? (
            <NwDropDownMenu
                size="small"
                actions={[
                    {
                        name: '',
                        key: 'edit',
                        pinned: true,
                        icon: byPrefixAndName.fasl['pen'],
                        action: () => setMode('EDIT'),
                    },
                    {
                        name: 'delete',
                        type: 'delete',
                        key: 'delete',
                        icon: byPrefixAndName.fasl['trash-alt'],
                        confirmationPlacement: 'left',
                        disabled: item.Type === 'Personal',
                        description: 'Personal connections cannot be deleted',
                        action: () => deleteElement(),
                    },
                    {
                        name: 'new address',
                        type: 'address',
                        key: 'address',
                        icon: byPrefixAndName.fass['plus-circle'],
                        action: () => handleCreateContactInfo('Address'),
                    },
                    {
                        name: 'new email',
                        type: 'email',
                        key: 'email',
                        icon: byPrefixAndName.fass['plus-circle'],
                        action: () => handleCreateContactInfo('Email'),
                    },
                    {
                        name: 'new phone',
                        type: 'phone',
                        key: 'phone',
                        icon: byPrefixAndName.fass['plus-circle'],
                        action: () => handleCreateContactInfo('Phone'),
                    },
                ]}
            />
        ) : (
            <div />
        );

    const handleCreateContactInfo = (key) => {
        setAddingNewItem(key);
    };

    const renderCard = () => {
        return (
            <NwCard title={ConnectionCardTitle()} extra={renderMenuSubItem()}>
                {item.Note && <FlexContainer mg="0 24px">{item.Note}</FlexContainer>}
                {item.Birthday && (
                    <FlexContainer mg="0 24px">
                        <NwIcon icon={byPrefixAndName.fasl['birthday-cake']} />
                        &nbsp;{showDateNoTime(item.Birthday)}
                    </FlexContainer>
                )}
                {item.Addresses.length > 0 && (
                    <>
                        <NwDivider>Addresses</NwDivider>
                        <BaseItemWrapper className="base-item">
                            <ul>
                                {item.Addresses.map((address) => (
                                    <li key={address.ID}>
                                        <BaseItem
                                            item={address}
                                            readOnly={readOnly}
                                            contactID={contactID}
                                            connectionID={item.ID}
                                            type="address"
                                        />
                                    </li>
                                ))}
                            </ul>
                        </BaseItemWrapper>
                    </>
                )}
                {item.Emails.length > 0 && (
                    <>
                        <NwDivider>E-mail</NwDivider>
                        <BaseItemWrapper className="base-item">
                            <ul>
                                {item.Emails.map((email) => (
                                    <li key={email.ID}>
                                        <BaseItem
                                            item={email}
                                            readOnly={readOnly}
                                            contactID={contactID}
                                            noMainIcon={item.TypeName === 'Cc'}
                                            connectionID={item.ID}
                                            type="email"
                                            onAddToMailingList={onAddToMailingList}
                                        />
                                    </li>
                                ))}
                            </ul>
                        </BaseItemWrapper>
                    </>
                )}
                {item.Phones.length > 0 && (
                    <>
                        <NwDivider>Phones</NwDivider>
                        <BaseItemWrapper className="base-item">
                            <ul>
                                {item.Phones.map((phone) => (
                                    <li key={phone.ID}>
                                        <BaseItem
                                            item={phone}
                                            contactID={contactID}
                                            readOnly={readOnly}
                                            connectionID={item.ID}
                                            type="phone"
                                        />
                                    </li>
                                ))}
                            </ul>
                        </BaseItemWrapper>
                    </>
                )}
                {mode === 'EDIT' && (
                    <NwDrawer
                        touchForm={touchForm}
                        onTouchForm={setTouchForm}
                        title={`Edit Connection`}
                        onClose={() => setMode('SHOW')}
                    >
                        <ConnectionDetailForm
                            contactID={item.ContactID}
                            connection={item}
                            connectionType={item.TypeName}
                            onTouchForm={setTouchForm}
                            onCancel={() => setMode('SHOW')}
                            onSave={() => setMode('SHOW')}
                        />
                    </NwDrawer>
                )}
                {addingNewItem && (
                    <NwDrawer
                        touchForm={touchForm}
                        onTouchForm={setTouchForm}
                        title={`Add ${addingNewItem}`}
                        onClose={() => setAddingNewItem(null)}
                    >
                        {addingNewItem === 'Address' && (
                            <AddressForm
                                connectionID={item.ID}
                                contactID={contactID}
                                onTouchForm={setTouchForm}
                                onCancel={() => setAddingNewItem(null)}
                                onSave={() => setAddingNewItem(null)}
                            />
                        )}
                        {addingNewItem === 'Email' && (
                            <EmailForm
                                contactID={contactID}
                                connectionID={item.ID}
                                onTouchForm={setTouchForm}
                                onCancel={() => setAddingNewItem(null)}
                                onSave={() => setAddingNewItem(null)}
                            />
                        )}
                        {addingNewItem === 'Phone' && (
                            <PhoneForm
                                contactID={contactID}
                                connectionID={item.ID}
                                onTouchForm={setTouchForm}
                                onCancel={() => setAddingNewItem(null)}
                                onSave={() => setAddingNewItem(null)}
                            />
                        )}
                    </NwDrawer>
                )}
            </NwCard>
        );
    };

    return (
        <Badge.Ribbon
            color={item.TypeName === 'Personal' ? StyleVariables.DangerColor : StyleVariables.PrimaryColor}
            text={<NwIcon icon={renderTypeIcon()} size="sm" />}
            placement="start"
        >
            {renderCard()}
        </Badge.Ribbon>
    );
};

export default ConnectionCard;
